/**
 * ATD - all facilities (provider Renata Silva, 2026-10-21: "a centralized view of all ATD updates across my
 * facilities without clicking into each facility ... quick access to APPROVED ATDs so I can do extra deliveries
 * while in the area ... how far I am from any ATD that has been updated or is still pending").
 *
 * One read (GET /emerge/day-sheet/atd, atdAllApi.ts) for every building the provider may open, grouped by building.
 * A resident is named only where the provider covers the building (or is assigned the resident); elsewhere the
 * building says how many and that they are not named here -- the server decides, this only draws it.
 *
 * Sort by distance asks the browser for the provider's position ONLY when the button is pressed, computes straight-
 * line distances here, and never sends, stores or logs the position. A building without recorded coordinates says
 * "Location not recorded" and sorts last; a refused or unavailable position says so and keeps the name order.
 *
 * Status is never colour alone: every chip carries its word and its own icon shape, and the legend names each.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowUpRight,
  CheckCircle2,
  CircleSlash,
  LocateFixed,
  MapPin,
  PackageCheck,
  RotateCcw,
  XCircle,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Button } from "../../../components/shadcn/ui/button";
import { StateBlock as TideState } from "../../../components/tide";
import { closureWords, shortDate } from "../providerApi";
import { Notice, Section } from "./FieldPrimitives";
import {
  ATD_LENSES,
  atdAllApi,
  isAtdLens,
  milesWords,
  sortByDistance,
} from "./atdAllApi";
import type { AtdAllFacilities, AtdBuilding, AtdLens, AtdResident, Position } from "./atdAllApi";
// Remind PA on the ATD view (ruling 7, 2026-10-27): the same component; the server says per resident whether it applies
// (an ATD-approved or dispensed resident is past the PA, so most rows here offer nothing).
import RemindPa from "./RemindPa";
import { STATE_MAX, paReminderApi } from "./paReminderApi";
import type { TwlRemind } from "./paReminderApi";
import "./field-workspace.css";
import "./field-atd.css";

const APPLE_PREFIX = "https://maps.apple.com/?q=";
const GOOGLE_PREFIX = "https://www.google.com/maps/search/?api=1&query=";

const LENS_LABEL: Record<AtdLens, string> = {
  approved: "Approved",
  // RULING, Isaiah Cotto, 2026-10-22: "Pending" means waiting on the provider's response -- approved, and nothing
  // arranged for the dispense yet. The query value stays `pending` so existing links keep working.
  pending: "Waiting on you",
  dispensed: "Dispensed recently",
  updated: "Updated recently",
};
const DAY_CHOICES = [7, 14, 30, 90];

/** Each ATD word: its words on screen, its shape, and its tone (tone is decoration; the word and the shape carry it). */
const STATUS: Record<string, { label: string; Icon: LucideIcon; tone: string }> = {
  YES: { label: "Approved", Icon: CheckCircle2, tone: "approved" },
  DISPENSED: { label: "Dispensed", Icon: PackageCheck, tone: "dispensed" },
  REFUSED: { label: "Refused", Icon: XCircle, tone: "refused" },
  REVERSAL: { label: "Reversed", Icon: RotateCcw, tone: "reversed" },
  ENDED: { label: "Not dispensing", Icon: CircleSlash, tone: "ended" },
};
const statusOf = (word: string | null | undefined) => {
  const w = String(word || "").trim().toUpperCase();
  if (STATUS[w]) return STATUS[w];
  return STATUS.ENDED;
};
/** The words "No", "NO-DECEASED" and "NO-MOVED" read as themselves under the Not dispensing shape. */
const statusWords = (word: string | null | undefined) => {
  const w = String(word || "").trim().toUpperCase();
  if (STATUS[w]) return STATUS[w].label;
  if (w === "NO-DECEASED") return "Not dispensing: deceased";
  if (w === "NO-MOVED") return "Not dispensing: moved";
  return w ? "Not dispensing" : "No ATD status";
};

function StatusChip({ word }: { word: string | null }) {
  const s = statusOf(word);
  return (
    <span className={`fw-atd-chip ${s.tone}`}>
      <s.Icon aria-hidden="true" />
      {statusWords(word)}
    </span>
  );
}

function Legend() {
  return (
    <div className="fw-atd-legend" role="note" aria-label="Legend">
      <strong>Legend</strong>
      <ul>
        {[["YES", "approved to dispense, not yet dispensed"], ["DISPENSED", "hearing aids dispensed"], ["REFUSED", "the resident refused"],
          ["REVERSAL", "a dispense or approval was reversed"], ["NO", "not dispensing (no, deceased or moved)"]].map(([word, meaning]) => (
          <li key={word}>
            <StatusChip word={word} /> <span>{meaning}</span>
          </li>
        ))}
        <li>
          <span className="fw-atd-chip planned"><LocateFixed aria-hidden="true" />Planned</span> <span>a visit or pit stop is set for the dispense</span>
        </li>
        <li>
          <strong>{LENS_LABEL.pending}</strong>{" "}
          <span>approved, and no visit or pit stop is set for the dispense yet (or the one set has passed or was cancelled)</span>
        </li>
      </ul>
      <p>Distances are straight-line miles from where you are when you press Sort by distance, not driving distance.</p>
    </div>
  );
}

function MapLinks({ maps, name }: { maps: AtdBuilding["maps"]; name: string }) {
  // Defense in depth: drawn only when the server's link is a maps search and nothing else.
  const apple = maps && typeof maps.apple === "string" && maps.apple.startsWith(APPLE_PREFIX) ? maps.apple : null;
  const google = maps && typeof maps.google === "string" && maps.google.startsWith(GOOGLE_PREFIX) ? maps.google : null;
  if (!apple && !google) return <p className="fw-muted">No map link is available for this building.</p>;
  return (
    <div className="fw-day-maps">
      {google && (
        <a className="fw-resource" href={google} target="_blank" rel="noopener noreferrer" aria-label={`Open ${name} in Google Maps`}>
          Google Maps <ArrowUpRight aria-hidden="true" />
        </a>
      )}
      {apple && (
        <a className="fw-resource" href={apple} target="_blank" rel="noopener noreferrer" aria-label={`Open ${name} in Apple Maps`}>
          Apple Maps <ArrowUpRight aria-hidden="true" />
        </a>
      )}
    </div>
  );
}

function planWords(r: AtdResident): string | null {
  const p = r.dispense_plan;
  if (!p) return null;
  if (p.state === "ON_MANIFEST") return p.visit_on ? `On the visit of ${shortDate(p.visit_on)}` : "On a planned visit";
  if (p.state === "PIT_STOP") return p.pit_stop_on ? `Pit stop ${shortDate(p.pit_stop_on)}` : "Pit stop planned";
  if (p.attention === "VISIT_PASSED" || p.attention === "PIT_STOP_PASSED") return "Planned date passed: no dispense planned";
  if (p.attention === "VISIT_CANCELLED") return "Planned visit cancelled: no dispense planned";
  return "No dispense planned";
}

function ResidentRow({ r, facilityId, remind, onRemind }: {
  r: AtdResident; facilityId: string; remind?: TwlRemind; onRemind: (patientId: string, next: TwlRemind) => void;
}) {
  const name = [r.first_name, r.last_name].filter(Boolean).join(" ") || "Name not recorded";
  const plan = planWords(r);
  const planned = r.dispense_plan && (r.dispense_plan.state === "ON_MANIFEST" || r.dispense_plan.state === "PIT_STOP");
  const isYes = String(r.atd_status || "").toUpperCase() === "YES";
  return (
    <li className="fw-atd-resident">
      <div>
        <strong>{name}</strong>
        <span className="fw-muted">Room {r.room_number || "not recorded"}</span>
      </div>
      <div className="fw-atd-resident-status">
        <StatusChip word={r.atd_status} />
        {planned && <span className="fw-atd-chip planned"><LocateFixed aria-hidden="true" />Planned</span>}
        <span className="fw-muted">
          {r.atd_date ? `${isYes ? "Approved" : "Since"} ${shortDate(r.atd_date)}` : "Date not recorded"}
          {plan && isYes ? ` · ${plan}` : ""}
        </span>
      </div>
      {remind && <RemindPa facilityId={facilityId} patientId={r.patient_id} residentName={name} remind={remind} onSent={(next) => onRemind(r.patient_id, next)} />}
    </li>
  );
}

type Load =
  | { kind: "loading" }
  | { kind: "ok"; data: AtdAllFacilities }
  | { kind: "unassigned" }
  | { kind: "failure"; status?: number; message: string };

type Locate =
  | { kind: "idle" }
  | { kind: "locating" }
  | { kind: "located"; at: Position }
  | { kind: "denied" }
  | { kind: "unavailable" };

export default function FieldAtdView() {
  const [params] = useSearchParams();
  const initial = params.get("atd");
  const [lens, setLens] = useState<AtdLens>(isAtdLens(initial) ? initial : "approved");
  const [days, setDays] = useState(14);
  const [load, setLoad] = useState<Load>({ kind: "loading" });
  const [locate, setLocate] = useState<Locate>({ kind: "idle" });
  const request = useRef(0);
  // A second notification link opened while this tab is showing: follow the new ?atd= lens.
  useEffect(() => { if (isAtdLens(initial)) setLens(initial); }, [initial]);

  const fetchIt = useCallback(async () => {
    const mine = ++request.current;
    setLoad({ kind: "loading" });
    try {
      const res = await atdAllApi.get(lens, days);
      if (mine !== request.current) return;
      if (res.success && res.data) setLoad({ kind: "ok", data: res.data });
      else if (res.status === 403 && /NO_CAPABILITY/.test(String(res.code || ""))) setLoad({ kind: "unassigned" });
      else setLoad({ kind: "failure", status: res.status, message: res.message || res.error || "The ATD list could not be loaded." });
    } catch {
      if (mine === request.current) setLoad({ kind: "failure", message: "The server could not be reached." });
    }
  }, [lens, days]);
  useEffect(() => { fetchIt(); }, [fetchIt]);
  useEffect(() => () => { request.current += 1; }, []);

  const onSortByDistance = () => {
    const geo = typeof navigator !== "undefined" ? navigator.geolocation : undefined;
    if (!geo || typeof geo.getCurrentPosition !== "function") {
      setLocate({ kind: "unavailable" });
      return;
    }
    setLocate({ kind: "locating" });
    geo.getCurrentPosition(
      (pos) => setLocate({ kind: "located", at: { latitude: pos.coords.latitude, longitude: pos.coords.longitude } }),
      (err) => setLocate({ kind: err && err.code === 1 ? "denied" : "unavailable" }),
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 300000 },
    );
  };

  const data = load.kind === "ok" ? load.data : null;
  const buildings = useMemo(() => {
    if (!data) return [];
    if (locate.kind === "located") return sortByDistance(data.buildings, locate.at);
    return data.buildings.map((b) => ({ ...b, distance: null as number | null }));
  }, [data, locate]);
  const sorted = locate.kind === "located";
  const lensInfo = data ? data.lenses.find((l) => l.key === lens) : null;

  // Remind PA (ruling 7): one state read per 100 named residents of the loaded page; drawn only where the server offers it.
  const [reminds, setReminds] = useState<Record<string, TwlRemind>>({});
  useEffect(() => {
    let live = true;
    setReminds({});
    if (!data) return () => { live = false; };
    const pairs = data.buildings.flatMap((b) => b.residents.map((r) => ({ facilityId: b.id, patientId: r.patient_id })));
    (async () => {
      const next: Record<string, TwlRemind> = {};
      for (let i = 0; i < pairs.length; i += STATE_MAX) {
        // eslint-disable-next-line no-await-in-loop
        const res = await paReminderApi.state(pairs.slice(i, i + STATE_MAX)).catch(() => null);
        if (!live || !res || !res.success || !res.data || !Array.isArray(res.data.items)) return;
        for (const it of res.data.items) if (it && it.patient_id && it.remind) next[it.patient_id.toLowerCase()] = it.remind;
      }
      if (live) setReminds(next);
    })();
    return () => { live = false; };
  }, [data]);
  const onRemind = useCallback((patientId: string, next: TwlRemind) => {
    setReminds((prev) => ({ ...prev, [patientId.toLowerCase()]: next }));
  }, []);
  const windowed = lens === "dispensed" || lens === "updated";

  return (
    <Section
      title="ATD across all your facilities"
      subtitle="Approved, waiting and recently changed ATD residents in every building you can open, without opening each one."
    >
      <div className="fw-panel-content fw-atd">
        <div className="fw-atd-controls">
          <div className="fw-atd-lenses" role="group" aria-label="Which ATD residents">
            {ATD_LENSES.map((key) => {
              const info = data ? data.lenses.find((l) => l.key === key) : null;
              return (
                <button
                  key={key}
                  type="button"
                  className={`fw-atd-lens${lens === key ? " active" : ""}`}
                  aria-pressed={lens === key}
                  onClick={() => setLens(key)}
                  title={info ? info.what : undefined}
                >
                  {LENS_LABEL[key]}
                  {info && <span className="fw-atd-count">{info.count}</span>}
                </button>
              );
            })}
          </div>
          {windowed && (
            <label className="fw-atd-days">
              <span>Within the last</span>
              <select value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Within the last how many days">
                {DAY_CHOICES.map((d) => <option key={d} value={d}>{d} days</option>)}
              </select>
            </label>
          )}
          <div className="fw-atd-sort">
            {sorted ? (
              <Button variant="outline" type="button" onClick={() => setLocate({ kind: "idle" })}>Sort by name</Button>
            ) : (
              <Button variant="outline" type="button" onClick={onSortByDistance} disabled={locate.kind === "locating"}>
                <MapPin aria-hidden="true" />
                {locate.kind === "locating" ? "Finding where you are…" : "Sort by distance"}
              </Button>
            )}
          </div>
        </div>
        <div aria-live="polite" className="fw-atd-locate-status">
          {sorted && <p>Nearest first, by straight-line distance from where you are now. Your location stays on this device.</p>}
          {locate.kind === "denied" && <p>Location permission was not given, so the list stays in name order. Allow location for this site to sort by distance.</p>}
          {locate.kind === "unavailable" && <p>Your location is not available on this device right now, so the list stays in name order.</p>}
        </div>
        {lensInfo && <p className="fw-muted fw-atd-what">{lensInfo.what.replace("the last N days", `the last ${data ? data.days : days} days`)}</p>}

        {load.kind === "loading" && <TideState kind="loading" shape="table" label="Loading ATD across your facilities…" />}
        {load.kind === "unassigned" && (
          <TideState kind="empty" reason="none" message="No building is assigned to you yet, so there is no ATD list to show. Ask for a facility assignment." />
        )}
        {load.kind === "failure" && <TideState kind="error" status={load.status} message={load.message} onRetry={fetchIt} />}

        {data && (
          <>
            {data.enterprise && (
              <Notice>
                Your access is organisation-wide. This view lists only buildings assigned to you by name; Scheduling&apos;s ATD queue lists every building.
              </Notice>
            )}
            {data.truncated && <Notice>More residents match than this page can show. Narrow the view or open a building&apos;s ATD tab.</Notice>}
            {buildings.length === 0 ? (
              <TideState
                kind="empty"
                reason="none"
                message={lens === "pending"
                  ? `No approved ATD is waiting on you across your ${data.scope_buildings} ${data.scope_buildings === 1 ? "building" : "buildings"}.`
                  : `No ${LENS_LABEL[lens].toLowerCase()} ATD residents across your ${data.scope_buildings} ${data.scope_buildings === 1 ? "building" : "buildings"}.`}
              />
            ) : (
              <ol className="fw-atd-buildings">
                {buildings.map((b) => {
                  const name = b.name || "Building name not recorded";
                  const withheld = b.withheld[lens] || 0;
                  return (
                    <li key={b.id}>
                      <article className="fw-atd-building" role="group" aria-label={`ATD at ${name}`}>
                        <header>
                          <div>
                            <h3>{name}</h3>
                            <p className="fw-muted">
                              {[b.city, b.state].filter(Boolean).join(", ") || "City not recorded"}
                              {b.closure ? ` · ${closureWords(b.closure)}` : ""}
                            </p>
                          </div>
                          <div className="fw-atd-distance">
                            {sorted ? (
                              b.distance === null ? <span>Location not recorded</span> : <strong>{milesWords(b.distance)} away</strong>
                            ) : b.latitude === null || b.longitude === null ? (
                              <span className="fw-muted">Location not recorded</span>
                            ) : null}
                          </div>
                        </header>
                        <p className="fw-atd-counts">
                          {ATD_LENSES.map((k) => `${LENS_LABEL[k]}: ${b.counts[k] || 0}`).join(" · ")}
                        </p>
                        {b.residents.length > 0 && (
                          <ul className="fw-atd-residents" aria-label={`${LENS_LABEL[lens]} residents at ${name}`}>
                            {b.residents.map((r) => (
                              <ResidentRow key={r.patient_id} r={r} facilityId={b.id} remind={reminds[r.patient_id.toLowerCase()]} onRemind={onRemind} />
                            ))}
                          </ul>
                        )}
                        {withheld > 0 && (
                          <p className="fw-atd-withheld">
                            {withheld} {withheld === 1 ? "resident here is" : "residents here are"} counted but not named: names show for
                            buildings you cover and residents assigned to you.
                          </p>
                        )}
                        <div className="fw-atd-actions">
                          <MapLinks maps={b.maps} name={name} />
                          <Link className="fw-resource" to={`/provider/${encodeURIComponent(b.id)}?view=manifest&lens=atd`}>
                            Open this building&apos;s ATD list <ArrowUpRight aria-hidden="true" />
                          </Link>
                        </div>
                      </article>
                    </li>
                  );
                })}
              </ol>
            )}
            {data.buildings_without > 0 && (
              <p className="fw-muted">
                {data.buildings_without} of your {data.scope_buildings} buildings have none in this view.
              </p>
            )}
          </>
        )}
        <Legend />
      </div>
    </Section>
  );
}
